import { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Clock, Shield, MessageSquareText, ArrowRight, Loader2,
  Copy, Film, Image, Filter, X, ChevronDown, ChevronRight, Repeat
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import CommandLayout from "@/components/CommandLayout";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ScriptFactoryModal from "@/components/ScriptFactoryModal";
import RehearsalHistory from "@/components/RehearsalHistory";

interface AnalysisReport {
  id: string;
  created_at: string;
  raw_text: string;
  manipulation_primary_type: string | null;
  manipulation_score: number;
  toxicity_score: number;
  grey_rock_response: string | null;
  viral_script: string | null;
  content_status: string;
  category: string;
}

type ScoreBand = "all" | "high" | "moderate" | "low";
type StatusFilter = "all" | "neutralized" | "scripted" | "exported";

interface GroupedReport {
  key: string;
  phrase: string;
  entries: AnalysisReport[];
  latest: AnalysisReport;
  count: number;
}

const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<AnalysisReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [scoreBand, setScoreBand] = useState<ScoreBand>("all");
  const [patternFilter, setPatternFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Script Factory
  const [scriptItem, setScriptItem] = useState<AnalysisReport | null>(null);

  const fetchReports = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("neutralizations")
      .select("id, created_at, raw_text, manipulation_primary_type, manipulation_score, toxicity_score, grey_rock_response, viral_script, content_status, category")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching reports:", error);
    } else {
      setReports((data as AnalysisReport[]) || []);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Unique patterns for filter dropdown
  const uniquePatterns = useMemo(() => {
    const set = new Set<string>();
    reports.forEach((r) => {
      if (r.manipulation_primary_type) set.add(r.manipulation_primary_type);
    });
    return Array.from(set).sort();
  }, [reports]);

  // Apply filters
  const filtered = useMemo(() => {
    return reports.filter((r) => {
      // Score band
      if (scoreBand === "high" && r.manipulation_score < 7) return false;
      if (scoreBand === "moderate" && (r.manipulation_score < 4 || r.manipulation_score >= 7)) return false;
      if (scoreBand === "low" && r.manipulation_score >= 4) return false;

      // Pattern
      if (patternFilter !== "all" && r.manipulation_primary_type !== patternFilter) return false;

      // Status
      if (statusFilter === "neutralized" && !r.grey_rock_response) return false;
      if (statusFilter === "scripted" && !r.viral_script) return false;
      if (statusFilter === "exported" && r.content_status === "backlog") return false;

      return true;
    });
  }, [reports, scoreBand, patternFilter, statusFilter]);

  // Group by normalized phrase
  const grouped = useMemo(() => {
    const map = new Map<string, AnalysisReport[]>();
    filtered.forEach((r) => {
      const key = r.raw_text.trim().toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    });
    const result: GroupedReport[] = [];
    map.forEach((entries, key) => {
      result.push({
        key,
        phrase: entries[0].raw_text,
        entries,
        latest: entries[0],
        count: entries.length,
      });
    });
    return result;
  }, [filtered]);

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const scoreColor = (score: number) =>
    score >= 7 ? "text-red-500" : score >= 4 ? "text-amber-400" : "text-cyan";

  const scoreBg = (score: number) =>
    score >= 7
      ? "bg-red-500/10 border-red-500/20"
      : score >= 4
        ? "bg-amber-400/10 border-amber-400/20"
        : "bg-cyan/10 border-cyan/20";

  const copyResponse = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast.success("Response copied");
  };

  const hasActiveFilters = scoreBand !== "all" || patternFilter !== "all" || statusFilter !== "all";

  const clearFilters = () => {
    setScoreBand("all");
    setPatternFilter("all");
    setStatusFilter("all");
  };

  const renderStatusBadges = (r: AnalysisReport) => {
    const badges = [];
    if (r.grey_rock_response) {
      badges.push(
        <button
          key="neut"
          onClick={(e) => { e.stopPropagation(); copyResponse(r.grey_rock_response!); }}
          title="Copy neutralized response"
        >
          <Badge className="gap-1 bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25 text-[10px] px-1.5 py-0.5 cursor-pointer transition-colors">
            <MessageSquareText className="w-2.5 h-2.5" />
            Response
          </Badge>
        </button>
      );
    }
    if (r.viral_script) {
      badges.push(
        <button
          key="script"
          onClick={(e) => { e.stopPropagation(); setScriptItem(r); }}
          title="Open in Script Factory"
        >
          <Badge className="gap-1 bg-purple-500/15 text-purple-400 border-purple-500/30 hover:bg-purple-500/25 text-[10px] px-1.5 py-0.5 cursor-pointer transition-colors">
            <Film className="w-2.5 h-2.5" />
            Script
          </Badge>
        </button>
      );
    }
    if (r.content_status !== "backlog") {
      badges.push(
        <Badge key="export" className="gap-1 bg-cyan/15 text-cyan border-cyan/30 text-[10px] px-1.5 py-0.5 opacity-70">
          <Image className="w-2.5 h-2.5" />
          Exported
        </Badge>
      );
    }
    if (badges.length === 0) {
      badges.push(
        <Badge key="analyzed" variant="outline" className="text-muted-foreground text-[10px] px-1.5 py-0.5">
          Analyzed
        </Badge>
      );
    }
    return badges;
  };

  const renderRow = (r: AnalysisReport, isNested = false) => {
    const date = new Date(r.created_at);
    const tactic = r.manipulation_primary_type
      ? r.manipulation_primary_type.replace(/_/g, " ")
      : "General";

    return (
      <div
        key={r.id}
        className={cn(
          "w-full text-left px-4 py-3 transition-colors group",
          isNested ? "bg-secondary/20 border-l-2 border-cyan/10" : "hover:bg-secondary/40"
        )}
      >
        {/* Desktop */}
        <div className="hidden md:grid grid-cols-[90px_1fr_120px_70px_auto_auto] gap-3 items-center">
          <span className="text-xs text-muted-foreground font-mono">
            {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
          </span>
          <button
            onClick={() => navigate("/analysis-result", { state: { analysisId: r.id, fromReports: true } })}
            className="text-sm text-foreground/80 truncate text-left hover:text-foreground transition-colors"
          >
            {r.raw_text.slice(0, 80)}{r.raw_text.length > 80 ? "…" : ""}
          </button>
          <span className="text-xs text-foreground/70 capitalize">{tactic}</span>
          <span className={cn("text-sm font-mono font-bold", scoreColor(r.manipulation_score))}>
            {r.manipulation_score}/10
          </span>
          <div className="flex items-center gap-1 flex-wrap">
            {renderStatusBadges(r)}
          </div>
          <div className="flex items-center gap-1 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/analysis-result", { state: { analysisId: r.id, fromReports: true } })}
              className="h-7 px-2 text-[11px] text-cyan hover:text-cyan hover:bg-cyan/10"
            >
              Report <ArrowRight className="w-3 h-3 ml-0.5" />
            </Button>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground font-mono">
              {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
            <div className="flex items-center gap-1.5">
              {renderStatusBadges(r)}
              <span className={cn("text-xs font-mono font-bold", scoreColor(r.manipulation_score))}>
                {r.manipulation_score}/10
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate("/analysis-result", { state: { analysisId: r.id, fromReports: true } })}
            className="text-sm text-foreground/80 line-clamp-2 text-left w-full"
          >
            {r.raw_text}
          </button>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-foreground/50 capitalize">{tactic}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/analysis-result", { state: { analysisId: r.id, fromReports: true } })}
                className="text-xs text-cyan font-semibold"
              >
                View →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <CommandLayout>
      <PageBreadcrumb items={[{ label: "Dashboard", path: "/dashboard" }, { label: "Reports" }]} />
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">
            <span className="text-foreground">Saved </span>
            <span className="text-gradient-cyan">Analyses</span>
          </h1>
          <p className="text-muted-foreground text-sm">
            Your analysis library — filter, review, and create content from past results.
          </p>
        </div>

        {/* Filter bar */}
        {reports.length > 0 && (
          <div className="space-y-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors",
                hasActiveFilters
                  ? "border-cyan/40 bg-cyan/10 text-cyan"
                  : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary/50"
              )}
            >
              <Filter className="w-3 h-3" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-cyan/20 text-[10px] font-bold">
                  {[scoreBand !== "all", patternFilter !== "all", statusFilter !== "all"].filter(Boolean).length}
                </span>
              )}
              <ChevronDown className={cn("w-3 h-3 transition-transform", showFilters && "rotate-180")} />
            </button>

            {showFilters && (
              <div className="flex flex-wrap gap-2 items-center p-3 rounded-lg border border-border bg-secondary/20">
                {/* Score band */}
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mr-1">Score</span>
                  {(["all", "high", "moderate", "low"] as ScoreBand[]).map((b) => (
                    <button
                      key={b}
                      onClick={() => setScoreBand(b)}
                      className={cn(
                        "px-2 py-1 rounded text-[10px] font-semibold border transition-colors",
                        scoreBand === b
                          ? b === "high" ? "border-red-500/40 bg-red-500/10 text-red-400"
                            : b === "moderate" ? "border-amber-400/40 bg-amber-400/10 text-amber-400"
                              : b === "low" ? "border-cyan/40 bg-cyan/10 text-cyan"
                                : "border-foreground/20 bg-foreground/5 text-foreground"
                          : "border-border text-muted-foreground hover:bg-secondary/40"
                      )}
                    >
                      {b === "all" ? "All" : b === "high" ? "7+" : b === "moderate" ? "4–6.9" : "< 4"}
                    </button>
                  ))}
                </div>

                {/* Pattern */}
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mr-1">Pattern</span>
                  <select
                    value={patternFilter}
                    onChange={(e) => setPatternFilter(e.target.value)}
                    className="px-2 py-1 rounded text-[10px] border border-border bg-secondary/30 text-foreground appearance-none cursor-pointer"
                  >
                    <option value="all">All</option>
                    {uniquePatterns.map((p) => (
                      <option key={p} value={p}>
                        {p.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div className="flex items-center gap-1">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mr-1">Status</span>
                  {(["all", "neutralized", "scripted", "exported"] as StatusFilter[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setStatusFilter(s)}
                      className={cn(
                        "px-2 py-1 rounded text-[10px] font-semibold border transition-colors capitalize",
                        statusFilter === s
                          ? "border-cyan/40 bg-cyan/10 text-cyan"
                          : "border-border text-muted-foreground hover:bg-secondary/40"
                      )}
                    >
                      {s === "all" ? "All" : s === "neutralized" ? "Has Response" : s === "scripted" ? "Has Script" : "Exported"}
                    </button>
                  ))}
                </div>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="w-8 h-8 text-cyan animate-spin" />
          </div>
        ) : reports.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-10 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-muted/50 border border-border mx-auto">
              <FileText className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <div className="space-y-1.5 max-w-sm mx-auto">
              <h3 className="text-lg font-semibold text-foreground">No Reports Yet</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Every analysis you run is saved here. Review past results, filter by pattern or score, and create content from your strongest findings.
              </p>
            </div>
            <button
              onClick={() => navigate("/dashboard")}
              className={cn(
                "inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm",
                "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                "hover:scale-[1.02] transition-transform"
              )}
            >
              <Shield className="w-4 h-4" />
              Run Your First Analysis
            </button>
            <p className="text-xs text-muted-foreground/60">Reports are generated automatically after each analysis</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-8 text-center">
            <Filter className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
            <p className="text-muted-foreground">No results match your filters.</p>
            <button onClick={clearFilters} className="text-cyan text-sm mt-2 hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {/* Table header */}
            <div className="hidden md:grid grid-cols-[90px_1fr_120px_70px_auto_auto] gap-3 px-4 py-2.5 border-b border-border bg-secondary/30">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Date</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Message</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Pattern</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Score</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Status</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono text-right">Actions</span>
            </div>

            <div className="divide-y divide-border">
              {grouped.map((g) => {
                const isExpanded = expandedGroups.has(g.key);
                const isDuplicate = g.count > 1;

                return (
                  <div key={g.key}>
                    {/* Primary row — always the latest entry */}
                    <div className="relative">
                      {isDuplicate && (
                        <button
                          onClick={() => toggleGroup(g.key)}
                          className="absolute left-1 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded flex items-center justify-center bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors hidden md:flex"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3 h-3" />
                          ) : (
                            <ChevronRight className="w-3 h-3" />
                          )}
                        </button>
                      )}
                      <div className={cn(isDuplicate && "md:pl-6")}>
                        {renderRow(g.latest)}
                      </div>
                      {isDuplicate && !isExpanded && (
                        <button
                          onClick={() => toggleGroup(g.key)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 hidden md:flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                        >
                          <Repeat className="w-3 h-3" />
                          {g.count - 1} more
                        </button>
                      )}
                    </div>

                    {/* Expanded duplicates */}
                    {isDuplicate && isExpanded && (
                      <div className="md:pl-6">
                        {g.entries.slice(1).map((r) => renderRow(r, true))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="px-4 py-2 border-t border-border bg-secondary/20 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground font-mono">
                {grouped.length} unique {grouped.length === 1 ? "phrase" : "phrases"} · {filtered.length} total {filtered.length === 1 ? "analysis" : "analyses"}
              </span>
              {hasActiveFilters && (
                <span className="text-[10px] text-cyan font-mono">
                  Filtered from {reports.length}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Rehearsal History */}
      <div className="mt-8">
        <RehearsalHistory />
      </div>

      {/* Script Factory Modal */}
      <ScriptFactoryModal
        open={!!scriptItem}
        onClose={() => setScriptItem(null)}
        item={scriptItem ? {
          id: scriptItem.id,
          raw_text: scriptItem.raw_text,
          toxicity_score: scriptItem.toxicity_score,
          manipulation_primary_type: scriptItem.manipulation_primary_type,
          grey_rock_response: scriptItem.grey_rock_response,
          created_at: scriptItem.created_at,
        } : null}
      />
    </CommandLayout>
  );
};

export default Reports;
